import { useState } from 'react';
import { Database, Filter, FileText, ChevronDown, ChevronUp, Info } from 'lucide-react';
import type { QueryFormData } from '../../../types/queryForm';

interface Step2ConfigurationProps {
  formData: QueryFormData;
  onChange: (data: Partial<QueryFormData>) => void;
  availableColumns: string[];
}

export default function Step2Configuration({
  formData,
  onChange,
  availableColumns
}: Step2ConfigurationProps) {
  const [showDocs, setShowDocs] = useState(true);

  const handleColumnToggle = (column: string) => {
    const newColumns = formData.selectedColumns.includes(column)
      ? formData.selectedColumns.filter(c => c !== column)
      : [...formData.selectedColumns, column];
    onChange({ selectedColumns: newColumns });
  };

  const handleFilterToggle = (column: string) => {
    const newFilters = formData.filterColumns.includes(column)
      ? formData.filterColumns.filter(c => c !== column)
      : [...formData.filterColumns, column];
    onChange({ filterColumns: newFilters });
  };

  const handleSelectAllColumns = () => {
    if (formData.selectedColumns.length === availableColumns.length) {
      onChange({ selectedColumns: [] });
    } else {
      onChange({ selectedColumns: [...availableColumns] });
    }
  };

  const columnsToShow = formData.selectedColumns.length > 0
    ? formData.selectedColumns
    : availableColumns;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Configuracoes</h3>
        <p className="text-sm text-gray-500">Defina quais campos serao retornados e quais poderao ser usados como filtro.</p>
      </div>

      {availableColumns.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Execute a query primeiro</p>
              <p className="text-sm text-amber-700 mt-1">
                Para configurar os campos e filtros, volte ao passo anterior e execute a query para descobrir as colunas disponiveis.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Campos Retornados</span>
              </div>
              <button
                type="button"
                onClick={handleSelectAllColumns}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {formData.selectedColumns.length === availableColumns.length ? 'Desmarcar todos' : 'Selecionar todos'}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Selecione quais campos serao incluidos na resposta. Se nenhum for selecionado, todos serao retornados.
            </p>
            <div className="flex flex-wrap gap-2">
              {availableColumns.map((col) => (
                <button
                  key={col}
                  type="button"
                  onClick={() => handleColumnToggle(col)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    formData.selectedColumns.includes(col)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {col}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900">Campos de Filtro</span>
            </div>
            <p className="text-xs text-gray-500">
              Selecione quais campos poderao ser usados como parametros de filtro na API.
            </p>
            <div className="flex flex-wrap gap-2">
              {availableColumns.map((col) => (
                <button
                  key={col}
                  type="button"
                  onClick={() => handleFilterToggle(col)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    formData.filterColumns.includes(col)
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                  }`}
                >
                  {col}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowDocs(!showDocs)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Documentacao da API</span>
          </div>
          {showDocs ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {showDocs && (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descricao para Documentacao
              </label>
              <textarea
                value={formData.documentationDescription}
                onChange={(e) => onChange({ documentationDescription: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Descreva o proposito desta API para a documentacao..."
              />
            </div>

            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <div className="text-xs font-mono">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs font-semibold">GET</span>
                  <span className="text-gray-300">/api/queries/{formData.endpointSlug || '{slug}'}</span>
                </div>

                <div className="text-gray-400 mb-2">Response Schema:</div>
                <pre className="text-green-400">
{`{
  "success": boolean,
  "data": [
    {
${columnsToShow.map(col => `      "${col}": any`).join(',\n')}
    }
  ],
  "metadata": {
    "total": number,
    "timestamp": string
  }
}`}
                </pre>

                {formData.filterColumns.length > 0 && (
                  <>
                    <div className="text-gray-400 mt-4 mb-2">Query Parameters:</div>
                    <div className="space-y-1">
                      {formData.filterColumns.map(col => (
                        <div key={col} className="flex items-center gap-2">
                          <span className="text-blue-400">{col}</span>
                          <span className="text-gray-500">:</span>
                          <span className="text-yellow-400">string</span>
                          <span className="text-gray-500 text-xs">(optional)</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
